
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CreditCard, Home, Car, Anchor, Wrench, Wallet } from "lucide-react";
import LoanExamplesSection from "@/components/home/LoanExamplesSection";
import { HebrewLoanTypeCard } from "@/components/home/HebrewLoanTypeCard";

const HebrewPage = () => {
  return (
    <div className="rtl-layout" dir="rtl">
      <section className="relative py-12 md:py-20 bg-gradient-to-r from-blue-700 to-blue-900 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">שירותי הלוואות בפינלנד</h1>
            <p className="text-xl text-blue-100 mb-8">
              השווה וקבל את הצעות ההלוואה הטובות ביותר בפינלנד. קבל עזרה בשפה שלך להבנת מערכת ההלוואות הפינית.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/he/apply" 
                className="bg-white hover:bg-blue-50 text-blue-700 px-6 py-3 rounded-lg font-semibold text-lg transition-colors shadow-lg"
                aria-label="הגש בקשה להלוואה וקבל הצעות ממספר בנקים"
                title="הגש בקשה עכשיו וקבל את הצעות ההלוואה הטובות ביותר בפינלנד"
              >
                הגש בקשה להלוואה
              </Link>
              <Link 
                to="/he/info" 
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
                aria-label="קרא עוד על מערכת ההלוואות בפינלנד"
                title="מידע חשוב על איך עובדות הלוואות בפינלנד"
              >
                קרא עוד על הלוואות
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 text-blue-700">סוגי הלוואות זמינים בפינלנד</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <HebrewLoanTypeCard
              title="הלוואת צרכן"
              description="מימון לרכישות יומיומיות"
              detailText="הלוואות צרכניות בפינלנד מאפשרות לך לממן רכישות גדולות כמו אלקטרוניקה וריהוט בשיעורי ריבית תחרותיות."
              icon={CreditCard}
              linkPath="/he/apply"
            />
            
            <HebrewLoanTypeCard
              title="משכנתא"
              description="מימון נוח לבתים"
              detailText="משכנתאות בפינלנד מאופיינות בשיעורי ריבית נמוכים יחסית ותקופות החזר ארוכות של עד 25 שנים."
              icon={Home}
              linkPath="/he/apply"
            />
            
            <HebrewLoanTypeCard
              title="הלוואת רכב"
              description="מימון למכונית חלומותיך"
              detailText="קבלת הלוואת רכב בפינלנד יכולה להיות חסכונית יותר מאשר אפשרויות המימון המוצעות על ידי סוכנויות רכב."
              icon={Car}
              linkPath="/he/apply"
            />
            
            <HebrewLoanTypeCard
              title="הלוואת סירה"
              description="לרכישה ותחזוקת סירות"
              detailText="הלוואות סירה מתוכננות במיוחד למימון רכישה, תיקון או שדרוג של סירות פנאי בפינלנד."
              icon={Anchor}
              linkPath="/he/apply"
            />
            
            <HebrewLoanTypeCard
              title="הלוואת שיפוץ"
              description="מימון לשיפורי בית"
              detailText="הלוואות שיפוץ עוזרות לך לממן תיקוני בית ושיפורים שמגדילים את ערך הנכס שלך בפינלנד."
              icon={Wrench}
              linkPath="/he/apply"
            />
            
            <HebrewLoanTypeCard
              title="איחוד הלוואות"
              description="איחוד הלוואות וחיסכון בעלויות"
              detailText="איחוד הלוואות מרובות להלוואה אחת יכול להפחית את התשלומים החודשיים שלך ולפשט את ניהול החובות שלך במערכת הפינית."
              icon={Wallet}
              linkPath="/he/apply"
            />
          </div>
        </div>
      </section>

      <LoanExamplesSection language="he" />

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-blue-700 mb-6 text-center">עזרה בשפה שלך</h2>
            <p className="text-gray-700 mb-4">
              אנו מבינים שהבנת מערכת ההלוואות במדינה חדשה יכולה להיות מאתגרת, במיוחד כאשר השפה היא מחסום. לכן אנו מספקים מידע ותמיכה בעברית.
            </p>
            <p className="text-gray-700 mb-6">
              אנחנו יכולים לעזור לך להבין:
            </p>
            <ul className="list-disc pr-5 mb-6 text-gray-700 space-y-2">
              <li>דרישות הזכאות להלוואה בפינלנד</li>
              <li>סוגי המסמכים הנדרשים</li>
              <li>כיצד להגיש בקשה למספר זהות פיני</li>
              <li>הבנת התנאים וההתניות של הלוואות פיניות</li>
              <li>זכויותיך ואחריותך כלווה בפינלנד</li>
            </ul>
            <div className="text-center">
              <Link 
                to="/he/apply" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                aria-label="הגש בקשה להלוואה עכשיו וקבל עזרה בשפה שלך"
                title="קבל הצעות הלוואה מותאמות אישית לצרכים הפיננסיים שלך"
              >
                הגש בקשה להלוואה עכשיו
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-blue-50 py-8 text-center">
        <Link 
          to="/" 
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          aria-label="חזור לדף הבית בפינית"
          title="עבור לגרסה הפינית של האתר"
        >
          <ArrowLeft className="ml-2" />
          חזור לדף הבית
        </Link>
      </div>
    </div>
  );
};

export default HebrewPage;
